import { Panel } from "../../../components/panels/Panel";
import { DiorDigitalTwin } from "./DiorDigitalTwin";
import { InputText } from "../../../components/forms/InputText";
import { ArrowLeftCircleIcon, CheckCircleIcon, CheckIcon, InboxIcon, NoSymbolIcon } from "@heroicons/react/20/solid";import { Select } from "../../../components/forms/Select";
import { patchData, postData, useGetData } from "../../../services/api/api-tools";
import { Loader } from "../../../components/loading/Loader";
import { useEffect, useState } from "react";
import { DiorSortableStepsList } from "./DiorSortableStepsList";
import { useAlert } from "../../../contexts/AlertContext";
import { useSocket } from "../../../contexts/SocketContext";
import { ActionButton } from "../../../components/buttons/ActionButton";

export const DiorLiveSetupPage = () => {

  const { sendEvent } = useSocket();
  const { showAlert } = useAlert();

  const newPatternPlaceholder = {
    id: -1,
    name: '',
    pattern_data: [{ name: 'scheme', display_text: 'scheme', value: 'json', json: [] }],
  };
  const [selectedPattern, setSelectedPattern] = useState(newPatternPlaceholder);
  const [steps, setSteps] = useState([]);
  const [newPatternName, setNewPatternName] = useState('');

  useEffect(() => {
    const schemePatternData = selectedPattern?.pattern_data?.find(({ name }) => name === 'scheme');

    if (!schemePatternData?.json?.length)
      setSteps([]);
    else
      setSteps(schemePatternData.json);
  }, [selectedPattern]);

  const { data: patterns, isLoading: patternsAreLoading, refetch: refetchPatterns } = useGetData('patterns', `patterns`);

  const handleSelectPattern = (pattern) => {
    setSelectedPattern(pattern);
    setNewPatternName(pattern.name);
  };

  const handlePoolClick = (pool, poolSchemeIndex) => {
    setSteps((steps) => [...steps, {
        id: crypto.randomUUID(), // useful to generate sortable list elements keys and prevent re-render
        pool_id: poolSchemeIndex,
        duration_min: 0, duration_max: 60
      }]
    );
  };

  const onChangeDuration = (stepId, option, value) => {
    setSteps(steps =>
      steps.map((item, i) => {
        if (item.id !== stepId) return item;

        const numericValue = Number(value) || 0;

        if (option === 'min') return { ...item, duration_min: numericValue };
        else if (option === 'max') return { ...item, duration_max: numericValue };

        return item;
      })
    );
  };

  const onDeleteStep = (stepId) => {
    setSteps(prev => prev.filter(step => step.id !== stepId));
  };

  const isValidProcessCode = (value) => /^[A-Za-z]{2}\d{3}$/.test(value);

  const checkValidDurations = () => !steps.some((step) => !(step.duration_min < step.duration_max));

  const getPatternData = (patternData, name) => patternData?.find((item) => item.name === name);

  const mergePatternData = (patternData, dataToMerge) => [
    ...(patternData ?? []).filter(({ name }) => name !== dataToMerge.name),
    dataToMerge,
  ];

  const isPatternDisabled = getPatternData(
    selectedPattern.pattern_data,
    'is_disabled'
  )?.value === '1';

  const savePattern = async () => {
    if (steps.length === 0){
      showAlert('error', 'Selecione pelo menos uma etapa.', undefined, 'Erro');
      return false;
    }

    if (selectedPattern.id === -1 && newPatternName === '') {
      showAlert('error', 'Informe um nome para o processo.', undefined, 'Erro');
      return false;
    }

    if (!isValidProcessCode(newPatternName)) {
      showAlert('error', 'Use o formato "LLNNN" (ex.: GA170).', undefined, 'Erro');
      return false;
    }

    if (!checkValidDurations()){
      showAlert('error', 'A duração mínima deve ser inferior à duração máxima.', undefined, 'Erro');
      return false;
    }

    try {
      // Creation of a new pattern
      if (selectedPattern.id === -1) {
        const body = {
          name: newPatternName.toUpperCase(),
          is_active: false,
          last_modified: new Date().toISOString(),
          commentary: '',
          pattern_event_types: [],
          workstation_id: 1,
          pattern_datas: [{ name: 'scheme', display_text: 'scheme', value: 'json', json: steps}],
        };

        const response = await postData('patterns', body);
      }
      // Update of an existing pattern
      else {
        const patternDatas = mergePatternData(
          selectedPattern.pattern_data,
          { name: 'scheme', display_text: 'scheme', value: 'json', json: steps }
        );
        await patchData(`patterns/${selectedPattern.id}/info`, { pattern_datas: patternDatas });
      }

      const { data: newPatterns } = await refetchPatterns();

      const patternToSelect = newPatterns.find((pattern) => pattern.name.toLowerCase() === newPatternName.toLowerCase());
      if (patternToSelect) setSelectedPattern(patternToSelect);

      // Send data to the engine
      sendEvent('current_data', { 'process_names': newPatterns.map((pattern) => pattern.name) });

      showAlert('success', 'O processo foi atualizado.', undefined, 'Sucesso');
    } catch (err) {
      showAlert('error', 'Ocorreu um erro.', undefined, 'Erro');
      console.error('Something bad happened:', err);
    }
  };

  const updatePatternState = async (isDisabled) => {
    const patternDatas = mergePatternData(
      selectedPattern.pattern_data,
      { name: 'is_disabled', display_text: 'is_disabled', value: isDisabled ? '1' : '0', json: null }
    );

    try {
      await patchData(`patterns/${selectedPattern.id}/info`, { pattern_datas: patternDatas });

      const { data: newPatterns } = await refetchPatterns();
      const updatedPattern = newPatterns.find(({ id }) => id === selectedPattern.id);

      if (updatedPattern) setSelectedPattern(updatedPattern);

      sendEvent('current_data', {
        process_names: newPatterns
          .filter((pattern) => getPatternData(pattern.pattern_data, 'is_disabled')?.value !== '1')
          .map((pattern) => pattern.name),
      });

      showAlert('success', `O processo foi ${isDisabled ? 'desativado' : 'ativado'}.`, undefined, 'Sucesso');
    } catch (err) {
      showAlert('error', 'Ocorreu um erro.', undefined, 'Erro');
      console.error('Something bad happened:', err);
    }
  };

  if (patternsAreLoading) return <Loader/>;

  return (
    <Panel title="CONFIGURAÇÃO DE PROCESSOS" category="main">
      <div className="h-full flex gap-4">
        <div className="h-full min-w-[600px] max-w-[600px]">
          <Panel title="DETALHES DO PROCESSO" className="rounded-md">
            {/* Process selection */}
            <Select
              items={patterns}
              value={selectedPattern}
              setValue={handleSelectPattern}
              itemToString={(item) => item?.name}
              placeholder="Criar novo processo"
              placeholderValue={newPatternPlaceholder}
              theme="dark"
              size="lg"
            />

            {
              selectedPattern.id === -1 &&
              <InputText theme="dark" size="lg" placeholder="Nome do processo"
                         value={newPatternName} setValue={setNewPatternName}
                         helper='Use o formato "LLNNN" (ex.: GA170)' className="w-full mt-4"/>
            }

            {/* Process steps */}
            <div className="h-full bg-perception-black-500 rounded-md mt-4 p-2 flex flex-col overflow-y-auto overflow-x-hidden">
              <DiorSortableStepsList
                steps={steps}
                onReorder={setSteps}
                onChangeDuration={onChangeDuration}
                onDeleteStep={onDeleteStep}
              />
              {
                steps.length === 0 &&
                <div className='flex flex-col items-center justify-center h-full text-perception-gray-600 text-xl text-center'>
                  <InboxIcon className='size-16' />
                  <span className='mt-2'>Nenhuma etapa definida</span>
                  <span className="text-sm">Clique em um banho para adicioná-lo ao processo selecionado.</span>
                </div>
              }
            </div>

            {/* Action buttons */}
            <div className="mt-4 flex justify-between">
              <ActionButton color="secondary" onClick={() => window.location = '/interface/live/1'} text="Voltar" Icon={ArrowLeftCircleIcon} size="md"/>

              <div className="flex gap-4">
                {selectedPattern?.id > 0 &&
                  <ActionButton
                    onClick={() => updatePatternState(!isPatternDisabled)}
                    text={isPatternDisabled ? 'Ativar' : 'Desativar'}
                    Icon={isPatternDisabled ? CheckIcon : NoSymbolIcon}
                    size="md"
                    color={isPatternDisabled ? 'success' : 'danger'}
                  />
                }
                <ActionButton onClick={savePattern} text="Salvar" Icon={CheckCircleIcon} size="md"/>
              </div>
            </div>
          </Panel>
        </div>
        <div className="h-full min-w-0 flex-1">
          <DiorDigitalTwin cartsList={[]} onPoolClick={handlePoolClick} />
        </div>
      </div>
    </Panel>
  );
};